/**
 * 虚拟横屏（#app-root.mobile-virtual-landscape 用 transform 旋转 90°）下的
 * 系统键盘适配。操作系统只认物理竖屏：键盘永远从物理底部（旋转后等于场景
 * 的侧边）弹出，弹窗里的表单被键盘横向盖住、输入框本身也是横躺的，且
 * visualViewport 收缩还会把旋转布局整体挤扁。这里做三件事：
 *
 * 1. 拦截 #app-root 内文本输入框的 focus，改在物理竖屏方向弹出一条正立的
 *    「代理输入条」，紧贴键盘上沿，输入实时回写真实输入框（input/change）；
 * 2. 代理期间 keyboardSheetActive()=true，createApp 据此冻结视口重算；
 * 3. 真正根治是原生横屏（orientation.lock 成功后键盘自然从场景底部弹出），
 *    由 main.ts 的重试负责；切到原生横屏时代理条自动收起。
 *
 * 竖屏登录页/业务页不在 #app-root 旋转范围内，不受影响。
 */

/** 会承接系统键盘的文本类输入框（滑杆/复选框等不走代理）。 */
const TEXT_INPUT_SELECTOR = [
    'input[type="text"]', 'input[type="password"]', 'input[type="email"]',
    'input[type="number"]', 'input[type="tel"]', 'input[type="search"]',
    'input[type="url"]', 'input:not([type])', 'textarea',
].join(',');

let sheet: HTMLElement | null = null;
let cleanupFns: Array<() => void> = [];

/** 代理输入条是否打开（createApp 冻结视口重算用）。 */
export function keyboardSheetActive(): boolean {
    return sheet !== null;
}

/** 找输入框的字段名：就近取表单里的 span 标签（如「房间名称」），退回占位符。 */
function fieldLabelOf(input: HTMLInputElement | HTMLTextAreaElement): string {
    const label = input.closest('.wc-field, label')?.querySelector('span')?.textContent?.trim();
    if (label) return label;
    return input.placeholder || '输入';
}

export function installPortraitKeyboardSheet(): void {
    const appRoot = document.getElementById('app-root');
    if (!appRoot) return;
    let switching = false;
    document.addEventListener('focusin', (e) => {
        const target = e.target;
        if (switching || !(target instanceof HTMLElement)) return;
        if (!appRoot.contains(target) || !target.matches(TEXT_INPUT_SELECTOR)) return;
        if (!appRoot.classList.contains('mobile-virtual-landscape')) return; // 桌面/原生横屏：键盘天然从场景底部弹出
        // 代理期间点另一个输入框：先收起再为新品打开（切焦点不掉键盘）。
        switching = true;
        try {
            closeSheet();
            openSheet(target as HTMLInputElement | HTMLTextAreaElement);
        } finally {
            switching = false;
        }
    }, true);
}

function openSheet(input: HTMLInputElement | HTMLTextAreaElement): void {
    const isArea = input instanceof HTMLTextAreaElement;
    const root = document.createElement('div');
    root.id = 'portrait-keyboard-sheet';
    const label = document.createElement('span');
    label.className = 'pks-label';
    label.textContent = fieldLabelOf(input);
    // iOS 聚焦 font-size<16px 的输入框会整页缩放，旋转布局会被放大打碎——代理框必须 ≥16px。
    const proxy = document.createElement(isArea ? 'textarea' : 'input') as HTMLInputElement | HTMLTextAreaElement;
    if (input instanceof HTMLInputElement && input.type) (proxy as HTMLInputElement).type = input.type;
    proxy.className = 'pks-field';
    proxy.value = input.value;
    if (input.maxLength > 0) proxy.maxLength = input.maxLength;
    proxy.placeholder = input.placeholder || '';
    proxy.enterKeyHint = 'done';
    if (isArea) (proxy as HTMLTextAreaElement).rows = Math.min(4, Math.max(2, input.rows || 3));
    const done = document.createElement('button');
    done.type = 'button';
    done.className = 'pks-done';
    done.textContent = '完成';
    root.append(label, proxy, done);
    document.body.append(root);
    sheet = root;

    const sync = () => {
        input.value = proxy.value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
    };
    const close = () => closeSheet();
    // pointerdown 阻止默认，避免点「完成」先抢走焦点触发 blur 竞态。
    done.addEventListener('pointerdown', (e) => e.preventDefault());
    done.addEventListener('click', () => {
        sync();
        input.dispatchEvent(new Event('change', { bubbles: true }));
        close();
    });
    proxy.addEventListener('input', sync);
    proxy.addEventListener('keydown', ((e: KeyboardEvent) => {
        if (e.key === 'Enter' && !isArea && !e.shiftKey) {
            e.preventDefault();
            done.click();
        }
    }) as EventListener);
    // 键盘被系统收起（返回手势/藏键盘按钮）时焦点常还在：定时哨兵兜底收起。
    proxy.addEventListener('blur', () => {
        setTimeout(() => { if (document.activeElement !== proxy) close(); }, 150);
    });

    // 紧贴键盘上沿：visualViewport 的高度/滚动实时跟随（iOS 布局视口不收缩，必须用 vv）。
    const vv = window.visualViewport;
    const position = () => {
        if (!sheet) return;
        const top = vv ? vv.offsetTop + vv.height - sheet.offsetHeight : window.innerHeight - sheet.offsetHeight;
        sheet.style.top = `${Math.max(0, Math.round(top))}px`;
    };
    vv?.addEventListener('resize', position);
    vv?.addEventListener('scroll', position);
    window.addEventListener('resize', position);
    // 弹窗已关（输入框脱离 DOM）或已切到原生横屏：代理条失去存在意义。
    const watchdog = window.setInterval(() => {
        if (!input.isConnected || !document.getElementById('app-root')?.classList.contains('mobile-virtual-landscape')) close();
    }, 400);
    cleanupFns = [
        () => vv?.removeEventListener('resize', position),
        () => vv?.removeEventListener('scroll', position),
        () => window.removeEventListener('resize', position),
        () => window.clearInterval(watchdog),
    ];

    // 交出真实输入框焦点（避免浏览器把横躺的它滚进可视区），代理框在同一点击
    // 手势内接管，键盘不掉。光标置于文本末尾，续改不打扰已有内容。
    input.blur();
    position();
    proxy.focus();
    const caret = proxy.value.length;
    if (proxy.setSelectionRange) proxy.setSelectionRange(caret, caret);
}

function closeSheet(): void {
    const root = sheet;
    sheet = null;
    for (const fn of cleanupFns.splice(0)) { try { fn(); } catch { /* ignore */ } }
    if (!root) return;
    const proxy = root.querySelector('.pks-field');
    root.remove();
    // 完成按钮路径已 blur；这里兜底（如哨兵触发）避免键盘挂着没焦点。
    if (proxy && proxy === document.activeElement) (proxy as HTMLElement).blur();
}
